import { useState, useEffect } from "react";
import { X, MapPin, Clock, ExternalLink, Users, GraduationCap, BookOpen, Info, CalendarDays } from "lucide-react";
import { fetchFormation } from "../../api/formation";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DetailStat({ label, value, accent }) {
  return (
    <div className="bg-gray-800 rounded-xl p-3 text-center">
      <div className={`text-lg font-bold ${accent || "text-white"}`}>{value}</div>
      <div className="text-[10px] text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function DetailSectionTitle({ icon: Icon, label }) {
  return (
    <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </h4>
  );
}

function DetailInfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between text-xs gap-3">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-300 font-medium text-right">{value}</span>
    </div>
  );
}

function Tag({ children, color = "gray" }) {
  const styles = {
    teal: "text-teal-300 bg-teal-500/10",
    blue: "text-blue-300 bg-blue-500/10",
    purple: "text-purple-300 bg-purple-500/10",
    green: "text-green-300 bg-green-500/10",
    orange: "text-orange-300 bg-orange-500/10",
    gray: "text-gray-400 bg-gray-700",
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full ${styles[color] || styles.gray}`}>
      {children}
    </span>
  );
}

// ─── Grade bins helpers ───────────────────────────────────────────────────────

function formatGradeBins(bins) {
  if (!bins || typeof bins !== "object") return null;
  const entries = Object.entries(bins)
    .filter(([, v]) => v != null && v > 0)
    .sort(([a], [b]) => parseFloat(a) - parseFloat(b));
  if (entries.length === 0) return null;
  return entries.map(([range, pct]) => (
    <div key={range} className="flex items-center gap-2 text-xs">
      <span className="text-gray-500 w-16 shrink-0">{range}</span>
      <div className="flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden">
        <div
          className="bg-teal-500 h-full rounded-full"
          style={{ width: `${Math.round(pct * 100)}%` }}
        />
      </div>
      <span className="text-gray-400 w-8 text-right">{Math.round(pct * 100)}%</span>
    </div>
  ));
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DipDetailPanel({ hit, onClose }) {
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hit?.objectID) return;
    setFormation(null);
    setLoading(true);
    fetchFormation(hit.objectID).then((data) => {
      setFormation(data);
      setLoading(false);
    });
  }, [hit?.objectID]);

  if (!hit) return null;

  // Merge hit (Algolia) with formation (Xano) — formation wins when available
  const d = formation ? { ...hit, ...formation } : hit;

  const fees = d.fees_min && d.fees_max
    ? d.fees_min === d.fees_max
      ? `${d.fees_min.toLocaleString("fr-FR")}€`
      : `${d.fees_min.toLocaleString("fr-FR")}€ - ${d.fees_max.toLocaleString("fr-FR")}€`
    : d.fees_max
    ? `≤ ${d.fees_max.toLocaleString("fr-FR")}€`
    : null;

  const admissionRate = d.admission_rate
    ? `${Math.round(d.admission_rate * 100)} %`
    : null;

  const city = d.city?.replace(/\s+arrondissement$/i, "") ?? null;

  const gradeBins = formatGradeBins(d.intake_bg_grade_average_bins);

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full z-50 bg-gray-900 border-l border-gray-800 w-full max-w-sm md:max-w-[400px] shadow-[-4px_0_32px_rgba(0,0,0,0.4)] flex flex-col">

        {/* Header */}
        <div className="shrink-0 bg-gray-800 px-5 pt-5 pb-4 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>

          {d.type_formation?.[0] && (
            <span className="text-[10px] text-teal-300 bg-teal-500/10 px-2 py-0.5 rounded-full mb-2 inline-block">
              {d.type_formation[0]}
            </span>
          )}

          <h2 className="text-base font-bold text-white leading-snug pr-8">{d.title}</h2>
          {d.school_title && (
            <p className="text-sm text-gray-400 mt-1">{d.school_title}</p>
          )}

          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            {city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {city}
              </span>
            )}
            {d.duration && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {d.duration}
              </span>
            )}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto job-scrollbar px-5 py-4 flex flex-col gap-5">

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-6 h-6 rounded-full border-2 border-teal-500/40 border-t-teal-500 animate-spin" />
              <span className="text-xs text-gray-500">Chargement…</span>
            </div>
          ) : (
            <>
              {/* Chiffres clés */}
              {(admissionRate || d.open_seats_last_year || d.candidates || d.success_rate_final) && (
                <div className="grid grid-cols-2 gap-2">
                  {admissionRate && (
                    <DetailStat label="taux d'admission" value={admissionRate} accent="text-teal-400" />
                  )}
                  {d.open_seats_last_year && (
                    <DetailStat label="places" value={d.open_seats_last_year} />
                  )}
                  {d.candidates && (
                    <DetailStat label="candidats" value={d.candidates.toLocaleString("fr-FR")} />
                  )}
                  {d.success_rate_final && (
                    <DetailStat label="taux de réussite" value={`${Math.round(d.success_rate_final * 100)} %`} accent="text-green-400" />
                  )}
                </div>
              )}

              {/* Parcoursup labels */}
              {d.parcoursup_labels?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {d.parcoursup_labels.map((label) => (
                    <Tag key={label} color="teal">{label}</Tag>
                  ))}
                </div>
              )}

              {/* Tags formation */}
              <div className="flex flex-wrap gap-1.5">
                {d.bac_type?.map((b) => (
                  <Tag key={b} color="blue">Bac {b}</Tag>
                ))}
                {d.apprentissage === "Obligatoire" && (
                  <Tag color="purple">Alternance disponible</Tag>
                )}
                {d.class_format && (
                  <Tag color="gray">{d.class_format}</Tag>
                )}
                {d.selective_program === "non_selective" && (
                  <Tag color="green">Non sélectif</Tag>
                )}
              </div>

              {/* Description */}
              {d.description && (
                <div>
                  <DetailSectionTitle icon={Info} label="La formation" />
                  <p className="text-sm text-gray-300 leading-relaxed">{d.description}</p>
                </div>
              )}

              {/* Profil attendu */}
              {(d.intake_doublettes_bg?.length > 0 || gradeBins) && (
                <div>
                  <DetailSectionTitle icon={GraduationCap} label="Profil attendu" />
                  {d.intake_doublettes_bg?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {d.intake_doublettes_bg.map((sp, i) => (
                        <Tag key={i} color="blue">
                          {Array.isArray(sp) ? sp.join(" / ") : sp}
                        </Tag>
                      ))}
                    </div>
                  )}
                  {gradeBins && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-[10px] text-gray-500 mb-1">Répartition des moyennes (Bac général)</p>
                      {gradeBins}
                    </div>
                  )}
                </div>
              )}

              {/* Matières enseignées */}
              {d.main_subjects?.length > 0 && (
                <div>
                  <DetailSectionTitle icon={BookOpen} label="Matières enseignées" />
                  <div className="flex flex-wrap gap-1.5">
                    {d.main_subjects.map((s) => (
                      <Tag key={s} color="gray">{s}</Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Infos pratiques */}
              {(d.class_format || d.apprentissage || d.application_channel || d.deadline || fees || d.taught_languages?.length > 0) && (
                <div>
                  <DetailSectionTitle icon={Info} label="Infos pratiques" />
                  <div className="flex flex-col gap-2">
                    <DetailInfoRow label="Format" value={d.class_format} />
                    <DetailInfoRow label="Alternance" value={d.apprentissage} />
                    <DetailInfoRow label="Candidature" value={d.application_channel} />
                    <DetailInfoRow label="Date limite" value={d.deadline} />
                    <DetailInfoRow label="Frais de scolarité" value={fees} />
                    {d.taught_languages?.length > 0 && (
                      <DetailInfoRow label="Langues" value={d.taught_languages.join(", ")} />
                    )}
                  </div>
                </div>
              )}

              {/* Portes ouvertes */}
              {d.information_sessions?.length > 0 && (
                <div>
                  <DetailSectionTitle icon={CalendarDays} label="Portes ouvertes" />
                  <div className="flex flex-col gap-1.5">
                    {d.information_sessions.map((session, i) => (
                      <div key={i} className="text-xs text-gray-300 bg-gray-800 rounded-lg px-3 py-2">
                        {typeof session === "string" ? session : session.date ?? JSON.stringify(session)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Liens */}
              <div className="flex flex-col gap-2 pb-1">
                {d.url_application && (
                  <a
                    href={d.url_application}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-xl transition"
                  >
                    Candidater sur Parcoursup
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {d.url_onisep && (
                  <a
                    href={d.url_onisep}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-xl transition border border-gray-700"
                  >
                    Fiche Onisep
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
